import React from "react";
import { Facebook, Instagram, Linkedin } from "lucide-react";
import { api, API } from "@/lib/api";

const TikTok = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true"><path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 0 1-2.59 2.5 2.59 2.59 0 0 1-2.59-2.59 2.59 2.59 0 0 1 3.36-2.47V9.68a5.67 5.67 0 0 0-6.45 5.63A5.67 5.67 0 0 0 9.85 21a5.67 5.67 0 0 0 5.69-5.69V9.01a7.34 7.34 0 0 0 4.29 1.38V7.3a4.28 4.28 0 0 1-3.23-1.48Z" /></svg>
);

export const NETS = {
  facebook: { label: "Facebook", icon: Facebook, color: "#1877F2" },
  instagram: { label: "Instagram", icon: Instagram, color: "#E1306C" },
  linkedin: { label: "LinkedIn", icon: Linkedin, color: "#0A66C2" },
  tiktok: { label: "TikTok", icon: TikTok, color: "#06060C" },
};

export const POST_STATUS = {
  draft: { label: "Draft", cls: "bg-[#F1F1F4] text-[#595D62]" },
  in_review: { label: "In review", cls: "bg-[#EDE7FF] text-[#5b2fd6]" },
  with_client: { label: "With client", cls: "bg-amber-50 text-amber-700" },
  approved: { label: "Approved", cls: "bg-[#E6F7EE] text-[#1f9d55]" },
  rejected: { label: "Rejected", cls: "bg-rose-50 text-rose-600" },
  scheduled: { label: "Scheduled", cls: "bg-sky-50 text-sky-700" },
};

export const STAGE_LABEL = { brief: "Brief review", draft: "Drafting", review: "Internal review", rework: "Revising", client: "With client", schedule: "Ready to schedule", done: "Scheduled" };

export const mediaUrl = (id) => `${API}/social-media/${id}`;
export const logoUrl = (ws) => (ws?.logo_path ? `${API}/workspaces/${ws.id}/logo?v=${encodeURIComponent(ws.logo_path)}` : null);
export const fmtWhen = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

export function StatusPill({ status, testid }) {
  const m = POST_STATUS[status] || POST_STATUS.draft;
  return <span data-testid={testid} className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium ${m.cls}`}>{m.label}</span>;
}

export function AccountAvatar({ a, ws, size = 36 }) {
  const n = NETS[a?.network] || NETS.facebook;
  const src = a?.avatar_id ? mediaUrl(a.avatar_id) : logoUrl(ws);
  return (
    <span className="relative inline-block shrink-0" style={{ width: size, height: size }}>
      {src ? <img src={src} alt="" className="h-full w-full rounded-full object-cover bg-[#F6F6FD] border border-white" />
        : <span className="h-full w-full rounded-full bg-[#06060C] text-white flex items-center justify-center text-xs font-semibold">{(a?.name || ws?.name || "?").slice(0, 1)}</span>}
      <span className="absolute -bottom-0.5 -right-0.5 h-[45%] w-[45%] min-h-[14px] min-w-[14px] rounded-full border-2 border-white flex items-center justify-center text-white" style={{ background: n.color }}>
        <n.icon className="h-[60%] w-[60%]" />
      </span>
    </span>
  );
}

export async function uploadSocialMedia(postId, file, onProgress) {
  const { data: init } = await api.post(`/console/social/posts/${postId}/media/init`, { filename: file.name, mime: file.type, size: file.size });
  const n = Math.ceil(file.size / init.chunk_size);
  for (let i = 0; i < n; i += 1) {
    const blob = file.slice(i * init.chunk_size, (i + 1) * init.chunk_size);
    await api.post(`/social-uploads/${init.upload_id}/chunk?index=${i}`, blob, { headers: { "Content-Type": "application/octet-stream" } });
    onProgress?.(Math.round(((i + 1) / n) * 100));
  }
  const { data } = await api.post(`/social-uploads/${init.upload_id}/complete`);
  return data;
}

export function MediaThumb({ m, className = "" }) {
  if (!m) return <span className={`block bg-[#F1F1F4] ${className}`} />;
  return m.kind === "video"
    ? <video src={`${mediaUrl(m.id)}#t=0.5`} muted preload="metadata" className={`object-cover bg-black ${className}`} />
    : <img src={mediaUrl(m.id)} alt="" className={`object-cover bg-[#F1F1F4] ${className}`} />;
}

export const isStory = (p) => p.post_type === "story";
export const formatOf = (p) => (isStory(p) ? "Story" : !p.media?.length ? "Text" : p.media.some((m) => m.kind === "video") ? "Video" : p.media.length > 1 ? "Carousel" : "Image");
